import React from "react";
import { Box, Typography, Avatar, Grid, ListItem, List } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from 'moment';
import RectangleIcon from "@mui/icons-material/Rectangle";

interface SolutionOwnerInfo {
    id: string;
    name: string;
    email?: string;
    googleProfilePicture?: string | null;
}

// Interface for Data Sources
interface DataSource {
    id: string;
    dataSourceName: string;
    projectId: string;
    location: string;
    lake: string;
    zone: string;
    status: 'pending' | 'active' | 'rejected' | string;
    solutionCategoryType: string;
    cloudProvider: string;
    updatedAt?: string;
    createdAt?: string;
    solutionOwner?: SolutionOwnerInfo | null;
    reasonForRejection?: string;
}

interface DataSourcesTableProps {
    dataSources: DataSource[];
    columnFilters: Record<string, boolean>;
    statusType?: 'pending' | 'active' | 'rejected';
}

const DataSourcesTable: React.FC<DataSourcesTableProps> = ({
    dataSources,
    columnFilters,
    statusType,
}) => {
    const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
    const navigate = useNavigate();

    
    const handleRowClick = (dataSourceId: string) => {
        navigate(`/datasolutions/data-sources/details/${dataSourceId}`);
    };

    const dateFormatter = (dateValue: string | number | null | undefined) => {
            if (!dateValue) return null;
            const numericDate = Number(dateValue);
            if (isNaN(numericDate)) return null;
            return moment(new Date(numericDate).toISOString()).format("DD/MM/YYYY | hh:mm A");
        };

    // Updated headers for Data Sources table - removed Application Name, redistributed widths
    const headers = [
        { key: "dataSourceName", label: "DATA SOURCE NAME", xs: 2.5 },
        { key: "projectId", label: "PROJECT ID", xs: 2 },
        { key: "cloudProvider", label: "CLOUD IDENTIFIER", xs: 2 },
        { key: "lake", label: "LAKE", xs: 1.5 },
        { key: "zone", label: "ZONE", xs: 1 },
        { key: "solutionOwner", label: "RESOURCE OWNER", xs: 1.5 },
        { key: "createdAt", label: "ADDED ON", xs: 1.5 },
    ];

    const iconColor = statusType === "active" ? "#12B76A" : statusType === "rejected" ? "#F04438" : "#FDB022";
    const listItemBgColor = statusType === "active" ? "#FFFFFF" : statusType === "rejected" ? "#F0443810" : "#FDB02210";


    return (
        <List sx={{ bgcolor: "transparent", px: 0, mx: 0, width: '100%' }}>
            {/* Table Header */}
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px", mb: 0.5, px: 2, py: 1, width: '100%' }}>
                <Box width="100%">
                    <Grid container direction="row" alignItems="center">
                        {headers.map(header => (
                            columnFilters[header.key] && (
                                <Grid item xs={header.xs as any} key={header.key}>
                                    <Typography fontSize="12px" color="#000000" fontFamily="Gilroy-Medium">
                                        {header.label}
                                    </Typography>
                                </Grid>
                            )
                        ))}
                    </Grid>
                </Box>
            </ListItem>

            {/* Table Body - DataSource Rows */}
            {dataSources.map((dataSource, index) => (
                <ListItem
                    key={dataSource.id}
                    sx={{
                        mt: 1,
                        borderRadius: "8px",
                        cursor: "pointer",
                        px: 2,
                        width: '100%',
                        backgroundColor: listItemBgColor,
                        '&:hover': {
                            borderColor: "black",
                        }
                    }}
                    onMouseEnter={() => setCardHoverIndex(index)}
                    onMouseLeave={() => setCardHoverIndex(null)}
                    onClick={() => handleRowClick(dataSource.id)}
                >
                    <Box width="100%">
                        <Grid container direction="row" alignItems="center">
                            {columnFilters.dataSourceName && (
                                <Grid item xs={headers.find(h => h.key === 'dataSourceName')?.xs as any}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <RectangleIcon
                                            sx={{
                                                color: iconColor,
                                                bgcolor: iconColor,
                                                width: "4px",
                                                height: "22px",
                                                marginRight: "8px",
                                            }}
                                        />
                                        <Typography fontSize="14px" fontFamily="Gilroy-Medium" color="text.primary" noWrap>
                                            {dataSource.dataSourceName || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.projectId && (
                                <Grid item xs={headers.find(h => h.key === 'projectId')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {dataSource.projectId || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.cloudProvider && (
                                <Grid item xs={headers.find(h => h.key === 'cloudProvider')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" sx={{textTransform: 'uppercase'}}>
                                        {dataSource.cloudProvider || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                             {columnFilters.lake && (
                                <Grid item xs={headers.find(h => h.key === 'lake')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
                                        {dataSource.lake || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                             {columnFilters.zone && (
                                <Grid item xs={headers.find(h => h.key === 'zone')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
                                        {dataSource.zone || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.solutionOwner && (
                                <Grid item xs={headers.find(h => h.key === 'solutionOwner')?.xs as any}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Avatar
                                            src={dataSource.solutionOwner?.googleProfilePicture || undefined}
                                            alt={dataSource.solutionOwner?.name || ""}
                                            sx={{ marginRight: "8px", height: 24, width: 24, fontSize: '0.75rem', bgcolor: dataSource.solutionOwner?.googleProfilePicture ? 'transparent' : 'rgba(0,0,0,0.1)' }}
                                        >
                                            {dataSource.solutionOwner && !dataSource.solutionOwner.googleProfilePicture ? dataSource.solutionOwner.name.charAt(0).toUpperCase() : null}
                                        </Avatar>
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                            {dataSource.solutionOwner?.name || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.createdAt && (
                                <Grid item xs={headers.find(h => h.key === 'createdAt')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
                                        {dateFormatter(Number(dataSource.createdAt))}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </ListItem>
            ))}
        </List>
    );
};

export default DataSourcesTable;



// import React from "react";
// import { Box, Typography, Avatar, Grid, ListItem, List } from "@mui/material";
// import { useNavigate } from "react-router-dom";
// import moment from 'moment';
// import RectangleIcon from "@mui/icons-material/Rectangle";

// interface SolutionOwnerInfo {
//     id: string;
//     name: string;
//     email?: string;
//     googleProfilePicture?: string | null;
// }

// // Interface for Data Sources
// interface DataSource {
//     id: string;
//     dataSourceName: string;
//     projectId: string;
//     location: string;
//     lake: string;
//     zone: string;
//     zoneType: string;
//     status: 'pending' | 'active' | 'rejected' | string;
//     environment?: string;
//     applicationName: string;
//     solutionCategoryType: string;
//     cloudProvider: string;
//     updatedAt?: string;
//     createdAt?: string;
//     solutionOwner?: SolutionOwnerInfo | null;
//     reasonForRejection?: string;
// }

// interface DataSourcesTableProps {
//     dataSources: DataSource[];
//     columnFilters: Record<string, boolean>;
//     statusType?: 'pending' | 'active' | 'rejected';
// }

// const DataSourcesTable: React.FC<DataSourcesTableProps> = ({
//     dataSources,
//     columnFilters,
//     statusType,
// }) => {
//     const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
//     const navigate = useNavigate();

    
//     const handleRowClick = (dataSourceId: string) => {
//         navigate(`/datasolutions/data-sources/details/${dataSourceId}`);
//     };

//     const dateFormatter = (dateValue: string | number | null | undefined) => {
//             if (!dateValue) return null;
//             const numericDate = Number(dateValue);
//             if (isNaN(numericDate)) return null;
//             return moment(new Date(numericDate).toISOString()).format("DD/MM/YYYY | hh:mm A");
//         };

//     // Updated headers for Data Sources table
//     const headers = [
//         { key: "dataSourceName", label: "DATA SOURCE NAME", xs: 2 },
//         { key: "projectId", label: "PROJECT ID", xs: 1.5 },
//         { key: "applicationName", label: "APPLICATION NAME", xs: 1.5 },
//         { key: "cloudProvider", label: "CLOUD IDENTIFIER", xs: 1.5 },
//         { key: "lake", label: "LAKE", xs: 1.5 },
//         { key: "zone", label: "ZONE", xs: 1 },
//         { key: "solutionOwner", label: "RESOURCE OWNER", xs: 1.5 },
//         { key: "createdAt", label: "ADDED ON", xs: 1.5 },
//     ];

//     const iconColor = statusType === "active" ? "#12B76A" : statusType === "rejected" ? "#F04438" : "#FDB022";
//     const listItemBgColor = statusType === "active" ? "#FFFFFF" : statusType === "rejected" ? "#F0443810" : "#FDB02210";


//     return (
//         <List sx={{ bgcolor: "transparent", px: 0, mx: 0, width: '100%' }}>
//             {/* Table Header */}
//             <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px", mb: 0.5, px: 2, py: 1, width: '100%' }}>
//                 <Box width="100%">
//                     <Grid container direction="row" alignItems="center">
//                         {headers.map(header => (
//                             columnFilters[header.key] && (
//                                 <Grid item xs={header.xs as any} key={header.key}>
//                                     <Typography fontSize="12px" color="#000000" fontFamily="Gilroy-Medium">
//                                         {header.label}
//                                     </Typography>
//                                 </Grid>
//                             )
//                         ))}
//                     </Grid>
//                 </Box>
//             </ListItem>

//             {/* Table Body - DataSource Rows */}
//             {dataSources.map((dataSource, index) => (
//                 <ListItem
//                     key={dataSource.id}
//                     sx={{
//                         mt: 1,
//                         borderRadius: "8px",
//                         cursor: "pointer",
//                         px: 2,
//                         width: '100%',
//                         backgroundColor: listItemBgColor,
//                         '&:hover': {
//                             borderColor: "black",
//                         }
//                     }}
//                     onMouseEnter={() => setCardHoverIndex(index)}
//                     onMouseLeave={() => setCardHoverIndex(null)}
//                     onClick={() => handleRowClick(dataSource.id)}
//                 >
//                     <Box width="100%">
//                         <Grid container direction="row" alignItems="center">
//                             {columnFilters.dataSourceName && (
//                                 <Grid item xs={headers.find(h => h.key === 'dataSourceName')?.xs as any}>
//                                     <Box sx={{ display: 'flex', alignItems: 'center' }}>
//                                         <RectangleIcon
//                                             sx={{
//                                                 color: iconColor,
//                                                 bgcolor: iconColor,
//                                                 width: "4px",
//                                                 height: "22px",
//                                                 marginRight: "8px",
//                                             }}
//                                         />
//                                         <Typography fontSize="14px" fontFamily="Gilroy-Medium" color="text.primary" noWrap>
//                                             {dataSource.dataSourceName || "N/A"}
//                                         </Typography>
//                                     </Box>
//                                 </Grid>
//                             )}
//                             {columnFilters.projectId && (
//                                 <Grid item xs={headers.find(h => h.key === 'projectId')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
//                                         {dataSource.projectId || "N/A"}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                             {columnFilters.applicationName && (
//                                 <Grid item xs={headers.find(h => h.key === 'applicationName')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
//                                         {dataSource.applicationName || "N/A"}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                             {columnFilters.cloudProvider && (
//                                 <Grid item xs={headers.find(h => h.key === 'cloudProvider')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" sx={{textTransform: 'uppercase'}}>
//                                         {dataSource.cloudProvider || "N/A"}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                              {columnFilters.lake && (
//                                 <Grid item xs={headers.find(h => h.key === 'lake')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
//                                         {dataSource.lake || "N/A"}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                              {columnFilters.zone && (
//                                 <Grid item xs={headers.find(h => h.key === 'zone')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
//                                         {dataSource.zone || "N/A"}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                             {columnFilters.solutionOwner && (
//                                 <Grid item xs={headers.find(h => h.key === 'solutionOwner')?.xs as any}>
//                                     <Box sx={{ display: "flex", alignItems: "center" }}>
//                                         <Avatar
//                                             src={dataSource.solutionOwner?.googleProfilePicture || undefined}
//                                             alt={dataSource.solutionOwner?.name || ""}
//                                             sx={{ marginRight: "8px", height: 24, width: 24, fontSize: '0.75rem', bgcolor: dataSource.solutionOwner?.googleProfilePicture ? 'transparent' : 'rgba(0,0,0,0.1)' }}
//                                         >
//                                             {dataSource.solutionOwner && !dataSource.solutionOwner.googleProfilePicture ? dataSource.solutionOwner.name.charAt(0).toUpperCase() : null}
//                                         </Avatar>
//                                         <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
//                                             {dataSource.solutionOwner?.name || "N/A"}
//                                         </Typography>
//                                     </Box>
//                                 </Grid>
//                             )}
//                             {columnFilters.createdAt && (
//                                 <Grid item xs={headers.find(h => h.key === 'createdAt')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
//                                         {dateFormatter(Number(dataSource.createdAt))}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                         </Grid>
//                     </Box>
//                 </ListItem>
//             ))}
//         </List>
//     );
// };

// export default DataSourcesTable;